import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageSquarePlus, Send, BookOpen, AlertTriangle, Plus, X, Tag, ChevronDown } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { motion, AnimatePresence } from 'framer-motion';

export const UserQASection = ({ extractedText, fileName, analysisComplete, apiBaseUrl }) => {
    const [userQAs, setUserQAs] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState('');
    const [isAsking, setIsAsking] = useState(false);
    const [error, setError] = useState(null);
    const [activeQA, setActiveQA] = useState(null);

    // User-defined category state
    const [userCategories, setUserCategories] = useState(['General']);
    const [selectedCategory, setSelectedCategory] = useState('General');
    const [filterCategory, setFilterCategory] = useState('All');
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');

    const inputRef = useRef(null);
    const categoryInputRef = useRef(null);

    // Reset Q&As when the document changes (new upload or clear)
    useEffect(() => {
        setUserQAs([]);
        setCurrentQuestion('');
        setError(null);
        setActiveQA(null);
        setUserCategories(['General']);
        setSelectedCategory('General');
        setFilterCategory('All');
    }, [extractedText]);

    // Auto-focus the new category input when it appears
    useEffect(() => {
        if (isAddingCategory) {
            setTimeout(() => categoryInputRef.current?.focus(), 50);
        }
    }, [isAddingCategory]);

    const handleAddCategory = () => {
        const trimmed = newCategoryName.trim();
        if (trimmed && !userCategories.includes(trimmed)) {
            setUserCategories(prev => [...prev, trimmed]);
            setSelectedCategory(trimmed);
        }
        setIsAddingCategory(false);
        setNewCategoryName('');
    };

    const handleDeleteCategory = (cat) => {
        if (userCategories.length === 1) return; // always keep at least one
        const remaining = userCategories.filter(c => c !== cat);
        setUserCategories(remaining);
        // Move any questions in the deleted category to the first remaining one
        setUserQAs(prev =>
            prev.map(qa => qa.userCategory === cat ? { ...qa, userCategory: remaining[0] } : qa)
        );
        if (selectedCategory === cat) setSelectedCategory(remaining[0]);
        if (filterCategory === cat) setFilterCategory('All');
    };

    const handleAsk = async () => {
        const trimmed = currentQuestion.trim();
        if (!trimmed || isAsking) return;

        setIsAsking(true);
        setError(null);

        try {
            const response = await axios.post(`${apiBaseUrl}/ask-question`, {
                question: trimmed,
                pdf_text: extractedText,
                file_name: fileName || null,
            });

            const { question, answer, section_reference, category } = response.data;

            setUserQAs(prev => [
                ...prev,
                {
                    id: Date.now(),
                    question,
                    answer,
                    sectionReference: section_reference,
                    category: category || 'General',   // AI-assigned (kept for reference)
                    userCategory: selectedCategory,     // user-defined category
                },
            ]);
            setCurrentQuestion('');
        } catch (err) {
            const message =
                err.response?.data?.detail ||
                err.message ||
                'Something went wrong. Please try again.';
            setError(message);
        } finally {
            setIsAsking(false);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAsk();
        }
    };

    const filteredQAs =
        filterCategory === 'All'
            ? userQAs
            : userQAs.filter(qa => qa.userCategory === filterCategory);

    if (!analysisComplete) return null;

    return (
        <>
            <div id="user-qa-input" className="animate-fade-in">
                {/* Section header */}
                <div className="flex items-center gap-2 mb-4">
                    <MessageSquarePlus className="h-5 w-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-slate-900">Ask Your Own Questions</h3>
                </div>

                {/* ── Category Management ── */}
                <div className="mb-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-medium text-slate-500 flex items-center gap-1 mr-1">
                            <Tag className="h-3 w-3" />
                            Categories:
                        </span>

                        {userCategories.map(cat => (
                            <span
                                key={cat}
                                onClick={() => setSelectedCategory(cat)}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer transition-all ${
                                    selectedCategory === cat
                                        ? 'bg-primary-600 text-white border-primary-600'
                                        : 'bg-white text-slate-600 border-slate-300 hover:border-primary-400 hover:text-primary-600'
                                }`}
                            >
                                {cat}
                                {userCategories.length > 1 && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat); }}
                                        className={`ml-0.5 rounded-full p-0.5 transition-colors ${
                                            selectedCategory === cat
                                                ? 'text-white/70 hover:text-white hover:bg-white/20'
                                                : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                                        }`}
                                        title={`Remove "${cat}"`}
                                    >
                                        <X className="h-2.5 w-2.5" />
                                    </button>
                                )}
                            </span>
                        ))}

                        {/* Inline add-category input */}
                        {isAddingCategory ? (
                            <input
                                ref={categoryInputRef}
                                type="text"
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleAddCategory();
                                    if (e.key === 'Escape') { setIsAddingCategory(false); setNewCategoryName(''); }
                                }}
                                onBlur={handleAddCategory}
                                placeholder="Category name…"
                                className="w-32 rounded-full border border-primary-400 bg-white px-2.5 py-1 text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                            />
                        ) : (
                            <button
                                onClick={() => setIsAddingCategory(true)}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-slate-500 border border-dashed border-slate-300 hover:border-primary-400 hover:text-primary-600 transition-all"
                                title="Add a new category"
                            >
                                <Plus className="h-3 w-3" />
                                Add
                            </button>
                        )}
                    </div>
                </div>

                {/* ── Input area ── */}
                <Card className="p-4 mb-4">
                    <div className="flex gap-2 items-center">
                        {/* Category selector */}
                        <div className="relative flex-shrink-0">
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="appearance-none rounded-lg border border-slate-300 bg-slate-50 pl-2.5 pr-6 py-2 text-xs text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer transition max-w-[110px]"
                                title="Select category for this question"
                            >
                                {userCategories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400 pointer-events-none" />
                        </div>

                        <input
                            ref={inputRef}
                            type="text"
                            value={currentQuestion}
                            onChange={(e) => setCurrentQuestion(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Ask about this document…"
                            disabled={isAsking}
                            className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-60 transition"
                        />
                        <Button
                            onClick={handleAsk}
                            disabled={!currentQuestion.trim() || isAsking}
                            isLoading={isAsking}
                            size="sm"
                        >
                            {!isAsking && <Send className="h-3.5 w-3.5 mr-1" />}
                            Ask
                        </Button>
                    </div>

                    <p className="mt-1.5 text-xs text-slate-400">
                        Adding to category:{' '}
                        <span className="font-medium text-primary-600">"{selectedCategory}"</span>
                    </p>

                    {error && (
                        <div className="mt-2 p-2 bg-danger-50 text-danger-700 rounded-lg flex items-start gap-2 text-xs">
                            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                            <span>{error}</span>
                        </div>
                    )}
                </Card>

                {/* ── Filter bar (shown only when there are questions) ── */}
                {userQAs.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap mb-3">
                        <span className="text-xs text-slate-400">Filter:</span>
                        {['All', ...userCategories].map(cat => (
                            <button
                                key={cat}
                                onClick={() => setFilterCategory(cat)}
                                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                                    filterCategory === cat
                                        ? 'bg-slate-700 text-white border-slate-700'
                                        : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400 hover:text-slate-700'
                                }`}
                            >
                                {cat}
                                {cat !== 'All' && (
                                    <span className="ml-1 opacity-60">
                                        {userQAs.filter(qa => qa.userCategory === cat).length}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                )}

                {/* ── Question pills ── */}
                {filteredQAs.length === 0 ? (
                    <div className="text-center py-6 text-slate-400 select-none">
                        <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-40" />
                        <p className="text-xs">
                            {userQAs.length === 0
                                ? 'No questions asked yet. Ask a question above to get AI-powered insights.'
                                : `No questions in the "${filterCategory}" category.`}
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        <AnimatePresence initial={false}>
                            {filteredQAs.map((qa) => (
                                <motion.button
                                    key={qa.id}
                                    initial={{ opacity: 0, scale: 0.85 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.85 }}
                                    transition={{ duration: 0.2 }}
                                    onClick={() => setActiveQA(qa)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200 hover:bg-primary-100 hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer"
                                >
                                    <span className="w-4 h-4 rounded-full bg-primary-600 text-white text-[10px] flex items-center justify-center flex-shrink-0 font-bold">
                                        {userQAs.indexOf(qa) + 1}
                                    </span>
                                    {qa.category || qa.userCategory}
                                </motion.button>
                            ))}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            <Modal
                isOpen={!!activeQA}
                onClose={() => setActiveQA(null)}
                title={activeQA?.category || activeQA?.userCategory || 'Question Detail'}
                size="md"
            >
                {activeQA && (
                    <div className="space-y-4">
                        <div>
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Question</p>
                            <p className="text-sm font-semibold text-slate-900">{activeQA.question}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Answer</p>
                            <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{activeQA.answer}</p>
                        </div>
                        {activeQA.sectionReference && activeQA.sectionReference !== 'N/A' && (
                            <div>
                                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Section Reference</p>
                                <Badge variant="secondary">📄 {activeQA.sectionReference}</Badge>
                            </div>
                        )}
                        <div className="flex flex-wrap gap-4">
                            {activeQA.category && (
                                <div>
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">AI Topic</p>
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                        <Tag className="h-3 w-3" />
                                        {activeQA.category}
                                    </span>
                                </div>
                            )}
                            <div>
                                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Your Category</p>
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200">
                                    <Tag className="h-3 w-3" />
                                    {activeQA.userCategory}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};
